import { createSlice } from '@reduxjs/toolkit';
import { enrichDocumentsWithLocalId } from 'helpers/document';
import {
	addSettingsFloorPlan,
	createDealershipAddress,
	deleteDealershipAddress,
	getDealershipAddresses,
	getDealershipInfo,
	getSettingsDocuments,
	getSettingsNotifications,
	getSettingsPaymentMethods,
	updateSettingsDefaultPaymentMethod,
	updateSettingsNotifications,
} from './thunks';

const addressInitialState = {
	data: [],
	currentPage: 0,
	totalPages: 0,
	fetchIsLoading: false,
	fetchErrorMessage: '',
	isCreationLoading: false,
	creationErrorMessage: '',
};

export const initialState = {
	dealershipInformation: {
		data: null,
		isLoading: false,
		errorMessage: '',
	},
	addresses: {
		...addressInitialState,
	},
	notifications: {
		data: {
			bids_enabled: false,
			bids_email_frequency: 'never',
			bids_sms_frequency: 'immediately',
			outbid_push_frequency: 'never',

			matches_enabled: false,
			matches_email_frequency: 'never',
			matches_sms_frequency: 'never',

			vehicles_sold_enabled: false,
			vehicle_sold_sms: false,
			vehicle_sold_push: false,
			vehicle_sold_email: false,

			strong_bids_enabled: false,
			strong_bid_sms: false,
			strong_bid_email: false,
			strong_bid_push: false,
		},
		isLoading: false,
		fetchSuccessful: false,
		errorMessage: '',
	},
	documents: {
		data: null,
		isLoading: false,
		fetchSuccessful: false,
		errorMessage: '',
	},
	paymentMethods: {
		isLoading: false,
		errorMessage: '',
		fetchSuccessful: false,
		isAddFloorPlanLoading: false,
		addFloorPlanErrorMessage: '',
		isUpdateDefaultPaymentMethodLoading: false,
		updateDefaultPaymentMethodErrorMessage: '',
		paymentMethodsNeedAttention: false,
		defaultPaymentMethod: { title: '', can_update_default_payment: false },
		ach: {
			permissions: {
				account_owner: true,
				can_manage: true,
			},
			pending_admin_verification: false,
			remove_guardrail: {
				destroyable: true,
				reason: null,
			},
			accounts: [],
			setup_options: {
				plaid: [
					{ value: 'buying', enabled: true },
					{ value: 'selling', enabled: true },
					{ value: 'buying_and_selling', enabled: true },
				],
				manual: [
					{ value: 'buying', enabled: true },
					{ value: 'selling', enabled: true },
					{ value: 'buying_and_selling', enabled: true },
				],
			},
		},
		floorPlans: {},
		afc: {
			state: '',
			links: {
				apply: '',
				learn_more: '',
				portal: '',
			},
		},
	},
};

const sortAddress = (a, b) => {
	const aHasDefaultType = Array.isArray(a.default_types) && a.default_types.length > 0;
	const bHasDefaultType = Array.isArray(b.default_types) && b.default_types.length > 0;

	if (aHasDefaultType && !bHasDefaultType) return -1;
	if (!aHasDefaultType && bHasDefaultType) return 1;
	return 0;
};

const sortAddresses = (addresses) => [...addresses].sort(sortAddress);

const areAddressIdsEqual = (firstId, secondId) => {
	if (!firstId || !secondId) return false;
	return firstId === secondId;
};

const removeDefaultTypesFromOtherAddresses = (addresses, addressId, defaultTypes) => {
	if (!defaultTypes.length) return addresses;

	return addresses.map((address) => {
		if (areAddressIdsEqual(address.id, addressId) || !Array.isArray(address.default_types)) return address;

		const defaultTypesWithoutDuplicates = address.default_types.filter(
			(defaultType) => !defaultTypes.includes(defaultType),
		);

		return defaultTypesWithoutDuplicates.length === address.default_types.length ?
				address
			:	{ ...address, default_types: defaultTypesWithoutDuplicates };
	});
};

const updateAddressInTheArray = (addressesArray, addressId, updatedData) => {
	const updatedAddressId = addressId ?? updatedData?.id;
	const updatedDefaultTypes = Array.isArray(updatedData?.default_types) ? updatedData.default_types : [];
	const addressesWithUpdatedAddress = addressesArray.map((address) => {
		if (areAddressIdsEqual(address.id, updatedAddressId)) {
			return { ...address, ...updatedData };
		}
		return address;
	});

	// Each transportation type can have only one default address.
	return sortAddresses(
		removeDefaultTypesFromOtherAddresses(addressesWithUpdatedAddress, updatedAddressId, updatedDefaultTypes),
	);
};

const insertAddressInTheArray = (addressesArray, addressData) => {
	const defaultTypes = Array.isArray(addressData.default_types) ? addressData.default_types : [];
	const addressesWithoutDuplicateDefaults = removeDefaultTypesFromOtherAddresses(
		addressesArray,
		addressData.id,
		defaultTypes,
	);

	return sortAddresses([...addressesWithoutDuplicateDefaults, addressData]);
};

const updateDocumentInTheArray = (documentsArray, documentLocalId, updatedDocument) => {
	let hasUpdatedDocument = false;
	return documentsArray.map((document) => {
		if (hasUpdatedDocument || document.localId !== documentLocalId) {
			return document;
		}
		hasUpdatedDocument = true;
		return { ...document, ...updatedDocument };
	});
};

export const settings = createSlice({
	name: 'settings',
	initialState,
	reducers: {
		updateDealership(state, action) {
			state.dealershipInformation.data = {
				...state.dealershipInformation.data,
				...action.payload,
			};
		},
		updateAddressInList(state, action) {
			const { updatedAddress } = action.payload;
			state.addresses.data = updateAddressInTheArray(state.addresses.data, updatedAddress.id, updatedAddress);
		},
		clearAddressErrors(state, action) {
			const { addressId } = action.payload;
			state.addresses.data = updateAddressInTheArray(state.addresses.data, addressId, {
				deleteErrorMessage: '',
				isDeleteInProgress: false,
			});
		},
		updateDocument(state, action) {
			const { document, updatedDocument } = action.payload || {};
			const documentsArray = state.documents.data?.data;
			const documentLocalId = document?.localId;
			if (!Array.isArray(documentsArray) || !updatedDocument || !documentLocalId) return;
			state.documents.data.data = updateDocumentInTheArray(documentsArray, documentLocalId, updatedDocument);
		},
		clearAddFloorPlanRequestState(state) {
			state.paymentMethods.isAddFloorPlanLoading = false;
			state.paymentMethods.addFloorPlanErrorMessage = '';
		},
		clearUpdateDefaultPaymentMethodRequestState(state) {
			state.paymentMethods.isUpdateDefaultPaymentMethodLoading = false;
			state.paymentMethods.updateDefaultPaymentMethodErrorMessage = '';
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(getDealershipInfo.pending, (state) => {
				state.dealershipInformation.isLoading = true;
				state.dealershipInformation.errorMessage = '';
			})
			.addCase(getDealershipInfo.fulfilled, (state, action) => {
				state.dealershipInformation.isLoading = false;
				state.dealershipInformation.data = action.payload;
				state.dealershipInformation.errorMessage = '';
			})
			.addCase(getDealershipInfo.rejected, (state, action) => {
				state.dealershipInformation.isLoading = false;
				state.dealershipInformation.data = null;
				state.dealershipInformation.errorMessage =
					action.error?.message || 'Failed to fetch dealership information';
			})

			.addCase(getDealershipAddresses.pending, (state, { meta }) => {
				const { page } = meta.arg;
				state.addresses.fetchIsLoading = true;
				state.addresses.fetchErrorMessage = '';
				if (!page || page === 1) {
					state.addresses.data = [];
					state.addresses.currentPage = 0;
					state.addresses.totalPages = 0;
				}
			})
			.addCase(getDealershipAddresses.fulfilled, (state, action) => {
				const page = action.payload.currentPage;
				state.addresses.fetchIsLoading = false;
				const addresses =
					!page || page === 1 ? action.payload.data : [...state.addresses.data, ...action.payload.data];
				state.addresses.data = sortAddresses(addresses);

				state.addresses.currentPage = page || 1;
				state.addresses.totalPages = action.payload.totalPages;
				state.addresses.fetchErrorMessage = '';
			})
			.addCase(getDealershipAddresses.rejected, (state, action) => {
				state.addresses.fetchIsLoading = false;
				state.addresses.fetchErrorMessage = action.error?.message || 'Failed to fetch dealership addresses';
			})

			.addCase(createDealershipAddress.pending, (state) => {
				state.addresses.isCreationLoading = true;
				state.addresses.creationErrorMessage = '';
			})
			.addCase(createDealershipAddress.fulfilled, (state, { payload }) => {
				state.addresses.isCreationLoading = false;
				state.addresses.creationErrorMessage = '';
				state.addresses.data = insertAddressInTheArray(state.addresses.data, payload);
			})
			.addCase(createDealershipAddress.rejected, (state, action) => {
				state.addresses.isCreationLoading = false;
				state.addresses.creationErrorMessage =
					action?.payload?.errorMessage || 'Failed to create dealership address';
			})

			.addCase(deleteDealershipAddress.pending, (state, { meta }) => {
				const { addressId } = meta.arg;
				state.addresses.data = updateAddressInTheArray(state.addresses.data, addressId, {
					isDeleteInProgress: true,
					deleteErrorMessage: '',
				});
			})
			.addCase(deleteDealershipAddress.fulfilled, (state, action) => {
				const { addressId } = action.payload;
				state.addresses.data = state.addresses.data.filter(
					(address) => !areAddressIdsEqual(address.id, addressId),
				);
			})
			.addCase(deleteDealershipAddress.rejected, (state, { meta, error }) => {
				const { addressId } = meta.arg;
				state.addresses.data = updateAddressInTheArray(state.addresses.data, addressId, {
					isDeleteInProgress: false,
					deleteErrorMessage: error?.message,
				});
			})

			.addCase(getSettingsNotifications.pending, (state) => {
				state.notifications.isLoading = true;
				state.notifications.errorMessage = '';
			})
			.addCase(getSettingsNotifications.fulfilled, (state, action) => {
				state.notifications.isLoading = false;
				state.notifications.data = action.payload;
				state.notifications.fetchSuccessful = true;
				state.notifications.errorMessage = '';
			})
			.addCase(getSettingsNotifications.rejected, (state, action) => {
				state.notifications.isLoading = false;
				state.notifications.errorMessage = action.error?.message || 'Failed to fetch notification preferences';
			})

			.addCase(updateSettingsNotifications.pending, (state) => {
				state.notifications.isLoading = true;
				state.notifications.errorMessage = '';
			})
			.addCase(updateSettingsNotifications.fulfilled, (state, action) => {
				state.notifications.isLoading = false;
				state.notifications.data = action.payload;
				state.notifications.errorMessage = '';
			})
			.addCase(updateSettingsNotifications.rejected, (state) => {
				state.notifications.isLoading = false;
			})

			.addCase(getSettingsDocuments.pending, (state) => {
				state.documents.isLoading = true;
				state.documents.errorMessage = '';
			})
			.addCase(getSettingsDocuments.fulfilled, (state, action) => {
				const documents = action.payload?.data;
				state.documents.isLoading = false;
				state.documents.data = {
					...action.payload,
					data: Array.isArray(documents) ? enrichDocumentsWithLocalId(documents) : [],
				};
				state.documents.fetchSuccessful = true;
				state.documents.errorMessage = '';
			})
			.addCase(getSettingsDocuments.rejected, (state, action) => {
				state.documents.isLoading = false;
				state.documents.fetchSuccessful = false;
				state.documents.errorMessage = action.error?.message || 'Failed to fetch documents';
			})

			.addCase(getSettingsPaymentMethods.pending, (state) => {
				state.paymentMethods.isLoading = true;
				state.paymentMethods.errorMessage = '';
			})
			.addCase(getSettingsPaymentMethods.fulfilled, (state, action) => {
				state.paymentMethods.isLoading = false;
				state.paymentMethods.fetchSuccessful = true;
				state.paymentMethods.errorMessage = '';

				const { default_payment, ach, floorplans, afc, payment_methods_need_attention } = action.payload.data;
				state.paymentMethods.defaultPaymentMethod = default_payment;
				state.paymentMethods.ach = ach || null;
				state.paymentMethods.paymentMethodsNeedAttention = payment_methods_need_attention;
				state.paymentMethods.floorPlans = floorplans;
				state.paymentMethods.afc.state = afc.state;
				state.paymentMethods.afc.company = afc.company;
				state.paymentMethods.afc.linkedAccount = afc.linked_account;
				state.paymentMethods.afc.links = afc.links;
			})
			.addCase(getSettingsPaymentMethods.rejected, (state, action) => {
				state.paymentMethods.isLoading = false;
				state.paymentMethods.fetchSuccessful = false;
				state.paymentMethods.errorMessage = action.error?.message || 'Failed to fetch payment methods';
			})
			.addCase(addSettingsFloorPlan.pending, (state) => {
				state.paymentMethods.isAddFloorPlanLoading = true;
				state.paymentMethods.addFloorPlanErrorMessage = '';
			})
			.addCase(addSettingsFloorPlan.fulfilled, (state, action) => {
				state.paymentMethods.isAddFloorPlanLoading = false;
				state.paymentMethods.addFloorPlanErrorMessage = '';

				const {
					floorplans,
					afc,
					payment_methods_need_attention: paymentMethodsNeedAttention,
				} = action.payload.data;
				state.paymentMethods.paymentMethodsNeedAttention = paymentMethodsNeedAttention;
				state.paymentMethods.floorPlans = floorplans;
				if (afc) {
					state.paymentMethods.afc.state = afc.state;
					state.paymentMethods.afc.company = afc.company;
					state.paymentMethods.afc.linkedAccount = afc.linked_account;
					state.paymentMethods.afc.links = afc.links;
				}
			})
			.addCase(addSettingsFloorPlan.rejected, (state, action) => {
				state.paymentMethods.isAddFloorPlanLoading = false;
				state.paymentMethods.addFloorPlanErrorMessage = action.error?.message;
			})
			.addCase(updateSettingsDefaultPaymentMethod.pending, (state) => {
				state.paymentMethods.isUpdateDefaultPaymentMethodLoading = true;
				state.paymentMethods.updateDefaultPaymentMethodErrorMessage = '';
			})
			.addCase(updateSettingsDefaultPaymentMethod.fulfilled, (state, action) => {
				const { default_payment, payment_methods_need_attention } = action.payload?.data || {};
				state.paymentMethods.isUpdateDefaultPaymentMethodLoading = false;
				state.paymentMethods.updateDefaultPaymentMethodErrorMessage = '';
				state.paymentMethods.paymentMethodsNeedAttention = payment_methods_need_attention;
				state.paymentMethods.defaultPaymentMethod = {
					...state.paymentMethods.defaultPaymentMethod,
					...default_payment,
				};
			})
			.addCase(updateSettingsDefaultPaymentMethod.rejected, (state, action) => {
				state.paymentMethods.isUpdateDefaultPaymentMethodLoading = false;
				state.paymentMethods.updateDefaultPaymentMethodErrorMessage = action.error?.message;
			});
	},
});

export const {
	updateDealership,
	updateAddressInList,
	clearAddressErrors,
	updateDocument,
	clearAddFloorPlanRequestState,
	clearUpdateDefaultPaymentMethodRequestState,
} = settings.actions;

export default settings.reducer;
